import { createContext, useContext, useState, useEffect, ReactNode, FC } from 'react'
import { useAuth } from './useAuth'
import { useProfiles } from './useProfiles'

type UserMode = 'guest' | 'host'

interface UserModeContextType {
  mode: UserMode
  setMode: (mode: UserMode) => void
  canSwitchToHost: boolean
}

const UserModeContext = createContext<UserModeContextType | undefined>(undefined)

interface UserModeProviderProps {
  children: ReactNode
}

export const UserModeProvider: FC<UserModeProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<UserMode>('guest')
  const { user } = useAuth()
  const { profile } = useProfiles()
  
  // Check if user can switch to host mode
  const canSwitchToHost = Boolean(user && profile?.host_approved)
  
  // Load saved mode from localStorage
  useEffect(() => {
    if (canSwitchToHost) {
      const savedMode = localStorage.getItem('user-mode') as UserMode
      if (savedMode === 'host' || savedMode === 'guest') {
        setModeState(savedMode)
      }
    } else {
      // Force guest mode if not approved host
      setModeState('guest')
    }
  }, [canSwitchToHost])
  
  const setMode = (newMode: UserMode) => {
    if (newMode === 'host' && !canSwitchToHost) {
      return // Prevent switching to host mode if not approved
    }
    
    setModeState(newMode)
    localStorage.setItem('user-mode', newMode)
  }
  
  return (
    <UserModeContext.Provider value={{ mode, setMode, canSwitchToHost }}>
      {children}
    </UserModeContext.Provider>
  )
}

export const useUserMode = () => {
  const context = useContext(UserModeContext)
  if (context === undefined) {
    throw new Error('useUserMode must be used within a UserModeProvider')
  }
  return context
}